import { useEffect, useState } from 'react';
import { api, ApiError, type Asset, type EditOp, type ModelInfo } from '../../lib/api';
import { sayQuote } from '../../lib/quick';
import { PICTURE_WAIT, sayWait, useElapsed } from '../../lib/waiting';
import { Thumb, displayName } from '../AssetGrid';
import { ModelPicker } from '../ModelPicker';
import { ModelOptions } from '../ModelOptions';
import { OpenInPro, QuoteLine, SendButton, Stage, WatchNote, useAssetUrl, useWatchAsset } from './parts';
import { useBench } from './useBench';
import type { TabProps } from './Quick';

// The Edit tab: a new picture from an old one.
//
// Four operations, each a chip: change it by a prompt, upscale it, take the
// background off, combine it with others. Every one files a NEW asset that
// names its parent; the source is never touched, so an edit that went wrong
// costs nothing but the edit. A redraw and a combine take the edit model
// from the picker (204); an upscale and a cutout are priced on the house
// rows for those, so the picker stands aside for them. An operation no
// model here offers says so on the stage and refuses the press.

const OPS: Array<{ id: EditOp; label: string; hint: string }> = [
  { id: 'prompt', label: 'Change it', hint: 'Say what should be different; the rest stays' },
  { id: 'upscale', label: 'Upscale', hint: 'The same picture at two or four times the size' },
  { id: 'remove-background', label: 'Remove background', hint: 'The subject on transparent' },
  { id: 'combine', label: 'Combine with…', hint: 'This picture and up to three others, in one' },
];
const MAX_REFS = 3;

export function QuickEdit({ brand, models, pass, onUsedPass, onOpenInPro, onError }: TabProps) {
  const [pictures, setPictures] = useState<Asset[]>([]);
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [op, setOp] = useState<EditOp>('prompt');
  const [scale, setScale] = useState<2 | 4>(2);
  const [prompt, setPrompt] = useState('');
  const [refs, setRefs] = useState<string[]>([]);
  const [madeId, setMadeId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const waited = useElapsed(startedAt);
  const brandId = brand?.id ?? null;
  // The edit models are the redraw's; the cutout row rides in the same list and is found by what it prices.
  const editModels = models.edit?.filter((m: ModelInfo) => m.defaultVariant !== 'remove-background');
  const { model, setModel, sel, setSel, quote, credits } = useBench('edit', editModels, () => ({ op, ...(op === 'upscale' ? { scale } : {}) }));
  const cutout = models.edit?.find((m) => m.defaultVariant === 'remove-background') ?? null;

  async function load() {
    try { const all = await api.assets(brandId); setPictures(all.filter((a) => a.kind === 'image' && a.status === 'ready')); }
    catch (e) { onError((e as Error).message); }
  }
  useEffect(() => { void load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [brandId]);

  // The picture handed from the Library or the Image tab is the source, once.
  useEffect(() => {
    if (!pass || pass.tab !== 'edit') return;
    setSourceId(pass.assetId); setMadeId(null);
    onUsedPass();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pass]);

  const source = pictures.find((a) => a.id === sourceId) ?? null;
  const offReason = (o: EditOp): string | null => {
    if (!models.edit) return null;
    if (o === 'remove-background' && !cutout) return 'No model here offers a cutout yet';
    if (o !== 'remove-background' && o !== 'upscale' && !editModels?.length) return 'No edit model is offered here yet';
    return null;
  };
  const off = offReason(op);
  const why = !source ? 'Pick a picture to start from'
    : off ? off
    : op === 'prompt' && prompt.trim().length < 5 ? 'Say what to change'
    : op === 'combine' && refs.length === 0 ? 'Pick a picture to combine with'
    : quote && !quote.balance.ok ? `Short by ${quote.balance.shortBy.toLocaleString()} credits`
    : null;

  const watch = useWatchAsset(madeId, brandId);
  const madeUrl = useAssetUrl(watch.asset);
  const sourceUrl = useAssetUrl(source);
  useEffect(() => { if (watch.asset?.status === 'ready') { setStartedAt(null); void load(); } /* eslint-disable-line react-hooks/exhaustive-deps */ }, [watch.asset?.status]);

  async function send() {
    if (!source) return;
    setBusy(true); setStartedAt(Date.now()); setMadeId(null); onError(null);
    try {
      const r = await api.editAsset(source.id, {
        op, brandId,
        ...(op === 'prompt' || op === 'combine' ? { ...(model ? { model: model.id } : {}), ...(sel.resolution ? { resolution: sel.resolution } : {}), prompt: prompt.trim() } : {}),
        ...(op === 'upscale' ? { scale } : {}),
        ...(op === 'combine' ? { references: refs } : {}),
      });
      setMadeId(r.asset.id);
    } catch (e) {
      const err = e as ApiError;
      onError(err.status === 409 ? `This studio cannot do that here yet — ${err.message}` : err.message);
      setStartedAt(null);
    } finally { setBusy(false); }
  }

  const what = op === 'upscale' ? `one upscale ×${scale}` : op === 'remove-background' ? 'one background removal' : op === 'combine' ? 'one combined picture' : 'one changed picture';
  return (
    <div className="quick-tab">
      <Stage empty={pictures.length ? 'Pick a picture below; the new one lands here.' : 'No pictures yet. Make one on Image first.'}>
        {madeUrl ? <img src={madeUrl} alt={what} />
          : off && source ? <p className="quick-stage-empty" data-tool="quick-edit-off">{off}</p>
          : sourceUrl ? <img src={sourceUrl} alt={source ? displayName(source) : ''} style={{ opacity: madeId ? 0.4 : 1 }} />
          : null}
      </Stage>
      <WatchNote asset={watch.asset} stalled={watch.stalled} lookAgain={watch.lookAgain} waiting={sayWait(busy, waited)} />
      {watch.asset?.status === 'ready' && source && <p className="sub-dim quick-note">A new picture, filed beside {displayName(source)} and naming it as its parent. The original is untouched.</p>}

      <div className="quick-chips" data-tool="quick-edit-source">
        <span className="sub-dim">Start from</span>
        <div className="quick-strip">
          {pictures.slice(0, 16).map((a) => (
            <button key={a.id} type="button" className={`quick-strip-item${sourceId === a.id ? ' on' : ''}`} title={displayName(a)} onClick={() => { setSourceId(a.id); setMadeId(null); setRefs((r) => r.filter((x) => x !== a.id)); }}>
              <Thumb asset={a} size={96} />
            </button>
          ))}
        </div>
      </div>

      <div className="quick-chips" role="radiogroup" aria-label="What to do" data-tool="quick-edit-ops">
        {OPS.map((o) => {
          const reason = offReason(o.id);
          return (
            <button key={o.id} type="button" role="radio" aria-checked={op === o.id} className={`chip${op === o.id ? ' on' : ''}`}
                    disabled={Boolean(reason)} title={reason ?? o.hint} onClick={() => setOp(o.id)}>{o.label}</button>
          );
        })}
        {op === 'upscale' && ([2, 4] as const).map((s) => (
          <button key={s} type="button" className={`chip${scale === s ? ' on' : ''}`} onClick={() => setScale(s)}>×{s}</button>
        ))}
      </div>

      {(op === 'prompt' || op === 'combine') && (
        <>
          <ModelPicker bench="edit" models={editModels ?? null} value={model} onChange={setModel} />
          <ModelOptions bench="edit" model={model} value={sel} onChange={setSel} />
          <textarea className="quick-prompt" value={prompt} rows={2} maxLength={500}
                    placeholder={op === 'prompt' ? 'Make it dusk, and take the car out of the drive' : 'How they go together — the two of them at the same table (optional)'}
                    onChange={(e) => setPrompt(e.target.value)} />
        </>
      )}
      {op === 'combine' && (
        <div className="quick-chips" data-tool="quick-edit-refs">
          <span className="sub-dim">Combine with</span>
          <div className="quick-strip">
            {pictures.filter((a) => a.id !== sourceId).slice(0, 16).map((a) => (
              <button key={a.id} type="button" className={`quick-strip-item${refs.includes(a.id) ? ' on' : ''}`} title={displayName(a)}
                      disabled={!refs.includes(a.id) && refs.length >= MAX_REFS}
                      onClick={() => setRefs((r) => (r.includes(a.id) ? r.filter((x) => x !== a.id) : [...r, a.id]))}>
                <Thumb asset={a} size={96} />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="quick-actions">
        <SendButton label={OPS.find((o) => o.id === op)!.label.replace('…', '')} busyLabel="Drawing…" busy={busy} credits={off ? null : credits} why={why} onClick={() => void send()} />
        <OpenInPro what="The Assets bench: the library with every control, and this prompt with it." onClick={() => onOpenInPro({
          bench: 'assets', move: 'assets-make',
          prefill: prompt.trim() ? { action: 'make_picture', say: '', args: { prompt: prompt.trim() }, bench: 'assets', move: 'assets-make', inline: false } : null,
        })} />
      </div>
      <QuoteLine text={off ? `${off}. Nothing can be charged for it.` : credits === null ? null : `${sayQuote(credits, what, quote?.balance)} ${PICTURE_WAIT}.`} />
    </div>
  );
}
